/**
 * BrandSearchDropdown — debounced search dropdown for TikTok brand selection.
 *
 * Renders as an iPad Settings row. On tap, expands to show a search input
 * and a dropdown with brand results. Debounces search by 300ms.
 * Shows authorization status for each brand.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Check, Lock, Search } from "lucide-react";
import { tiktokProductApi } from "../api/tiktokProductApi";
import {
  useTikTokEditStore,
  selectFieldValue,
  selectIsFieldDirty,
} from "../stores/useTikTokEditStore";

interface BrandSearchDropdownProps {
  connectionName: string;
  categoryId: string;
  currentBrandName: string | undefined;
  onSelect: (brandId: string, brandName: string) => void;
}

export function BrandSearchDropdown({
  connectionName,
  categoryId,
  currentBrandName,
  onSelect,
}: BrandSearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const isBrandDirty = useTikTokEditStore(selectIsFieldDirty("brand_id"));
  const dirtyBrandName = useTikTokEditStore(selectFieldValue("brand_name"));
  const displayName = dirtyBrandName || currentBrandName || "\u2014";

  // Debounce search input by 300ms
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [searchText]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchText("");
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  const { data, isLoading } = useQuery({
    queryKey: ["tiktok-brands", connectionName, categoryId, debouncedSearch],
    queryFn: () =>
      tiktokProductApi.getBrands(connectionName, {
        category_id: categoryId,
        brand_name: debouncedSearch || undefined,
      }),
    enabled: open && debouncedSearch.length > 0 && !!connectionName,
    staleTime: 60_000,
  });

  const brands = data?.brands ?? [];

  const handleSelect = useCallback(
    (brandId: string, brandName: string) => {
      onSelect(brandId, brandName);
      setOpen(false);
      setSearchText("");
    },
    [onSelect],
  );

  return (
    <div ref={containerRef} className="relative">
      {/* Row — tappable to open */}
      <button
        type="button"
        className="flex items-center gap-4 px-4 py-5 w-full text-left hover:bg-accent/50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span
          className="text-lg w-1/2"
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          Brand
        </span>
        <div className="text-lg w-1/2 text-right text-foreground flex items-center justify-end gap-1">
          <span className={`break-words whitespace-pre-wrap ${isBrandDirty ? "font-medium" : ""}`}>
            {displayName}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 left-0 right-0 mx-4 mt-1 bg-card rounded-sm shadow-lg border border-border/60 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search brands..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>

          {/* Results */}
          <div className="max-h-60 overflow-y-auto">
            {debouncedSearch.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                Type to search brands
              </div>
            )}

            {debouncedSearch.length > 0 && isLoading && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                Searching...
              </div>
            )}

            {debouncedSearch.length > 0 && !isLoading && brands.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                No brands found
              </div>
            )}

            {brands.map((brand) => {
              const isAuthorized = brand.authorized_status === "authorized" || brand.is_t1_brand;
              return (
                <button
                  key={brand.id}
                  type="button"
                  className={`flex items-center gap-3 w-full px-3 py-3 text-left hover:bg-accent/50 transition-colors ${
                    !isAuthorized ? "opacity-50" : ""
                  }`}
                  onClick={() => handleSelect(brand.id, brand.name)}
                >
                  <span className="flex-1 text-sm truncate">{brand.name}</span>
                  {!isAuthorized && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                      <Lock className="h-3 w-3" />
                      Not authorized
                    </span>
                  )}
                  {isAuthorized && (
                    <Check className="h-4 w-4 text-green-600 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
